import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FaHourglassHalf } from 'react-icons/fa';

const ContactContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: -4.5rem;
  padding: 10.5rem 1.5rem 5rem;
  background: rgb(var(--bg));
  color: rgb(var(--fg) / 0.92);
  min-height: 100vh;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 10% 0%, rgba(69, 82, 109, 0.18), transparent 40%),
      radial-gradient(circle at 90% 10%, rgba(107, 120, 139, 0.1), transparent 35%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 9.5rem 1rem 4rem;
  }
`;

const ContactIntro = styled.div`
  width: 100%;
  max-width: 760px;
  text-align: center;
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  margin: 0 0 0.7rem 0;
  font-size: 2.6rem;
  line-height: 1.08;
  letter-spacing: -0.03em;
  color: rgb(var(--fg) / 0.95);
  font-weight: 800;

  span {
    color: rgb(var(--gray));
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const IntroText = styled.p`
  margin: 0;
  font-size: 1rem;
  line-height: 1.85;
  color: rgb(var(--gray) / 0.7);
  max-width: 680px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 0.94rem;
    line-height: 1.75;
  }
`;

const Form = styled.form`
  background: rgb(var(--fg) / 0.035);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  border-radius: 20px;
  border: 1px solid rgb(var(--fg) / 0.06);
  padding: 1.6rem;
  width: 100%;
  max-width: 720px;
  position: relative;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);

  @media (max-width: 768px) {
    padding: 1.1rem;
    border-radius: 18px;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.85rem;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
`;

const Label = styled.label`
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgb(var(--gray));
  margin-bottom: 0.45rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.9rem 1rem;
  border: 1px solid rgb(var(--fg) / 0.08);
  border-radius: 12px;
  font-size: 1rem;
  background: rgb(var(--fg) / 0.04);
  color: rgb(var(--fg) / 0.92);
  box-sizing: border-box;
  transition: border-color 0.2s ease, background 0.2s ease;

  &::placeholder {
    color: rgb(var(--gray) / 0.4);
  }

  &:focus {
    outline: none;
    border-color: rgb(var(--fg) / 0.24);
    background: rgb(var(--fg) / 0.06);
  }

  &:disabled {
    background: rgb(var(--fg) / 0.02);
    color: rgb(var(--gray) / 0.4);
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.95rem 1rem;
  border: 1px solid rgb(var(--fg) / 0.08);
  border-radius: 12px;
  font-size: 1rem;
  background: rgb(var(--fg) / 0.04);
  color: rgb(var(--fg) / 0.92);
  box-sizing: border-box;
  resize: vertical;
  min-height: 160px;
  font-family: inherit;
  transition: border-color 0.2s ease, background 0.2s ease;

  &::placeholder {
    color: rgb(var(--gray) / 0.4);
  }

  &:focus {
    outline: none;
    border-color: rgb(var(--fg) / 0.24);
    background: rgb(var(--fg) / 0.06);
  }

  &:disabled {
    background: rgb(var(--fg) / 0.02);
    color: rgb(var(--gray) / 0.4);
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  background: rgb(var(--fg) / 0.06);
  color: rgb(var(--fg) / 0.92);
  padding: 0.9rem 1.2rem;
  border: 1px solid rgb(var(--fg) / 0.14);
  border-radius: 999px;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.25s ease, border-color 0.25s ease, background 0.25s ease;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgb(var(--fg) / 0.28);
  }

  &:disabled {
    background: rgb(var(--fg) / 0.02);
    color: rgb(var(--gray) / 0.4);
    border-color: rgb(var(--fg) / 0.05);
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingIcon = styled.div`
  margin-right: 10px;
  display: flex;
  align-items: center;
  animation: pulse 1s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% { opacity: 0.65; }
    50% { opacity: 1; }
  }
`;

const ThankYouMessage = styled.div`
  text-align: center;
  font-size: 1.08rem;
  line-height: 1.8;
  font-weight: 500;
  color: rgb(var(--fg) / 0.85);
  background: rgb(var(--fg) / 0.035);
  padding: 2rem;
  border-radius: 20px;
  border: 1px solid rgb(var(--fg) / 0.06);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  max-width: 680px;

  strong {
    color: rgb(var(--gray));
    display: block;
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
    font-weight: 700;
  }
`;

const AltContact = styled.p`
  text-align: center;
  margin-top: 1rem;
  color: rgb(var(--gray) / 0.7);
  line-height: 1.75;
  font-size: 0.96rem;

  a {
    color: rgb(var(--gray));
    text-decoration: none;
    font-weight: 600;
    transition: color 0.3s;
  }

  a:hover {
    color: rgb(var(--fg) / 0.92);
  }
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    website: '' // honeypot field
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    const additionalData = {
      browser: navigator.userAgent,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      language: navigator.language,
      platform: navigator.platform,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timestamp: new Date().toISOString(),
    };

    const finalData = { ...formData, ...additionalData };

    fetch('https://us-central1-portfolio-8fb07.cloudfunctions.net/sendEmail', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(finalData),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          console.log('Email sent successfully!', data.message);
          setSubmitted(true);
          setLoading(false);
          setTimeout(() => {
            navigate('/');
          }, 5000);
        } else {
          console.error('Error sending email:', data.message);
          setLoading(false);
          alert('Failed to submit the form. Please try again.');
        }
      })
      .catch((error) => {
        console.error('Error sending email:', error);
        setLoading(false);
        alert('Failed to submit the form. Please try again.');
      });
  };

  return (
    <ContactContainer>
      {submitted ? (
        <ThankYouMessage>
          <strong>Message received.</strong>
          Thank you for reaching out. I will try to get back to you soon.<br />
          Redirecting to the homepage...
        </ThankYouMessage>
      ) : (
        <>
          <ContactIntro>
            <Title>Let’s <span>connect.</span></Title>
            <IntroText>
              Whether you want to discuss a project, collaboration, opportunity, or just reach out directly, feel free to send a message below.
            </IntroText>
          </ContactIntro>
          <Form onSubmit={handleSubmit}>
            {/* Honeypot field - hidden from users */}
            <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
              <label>Leave this field empty</label>
              <input
                type="text"
                name="website"
                value={formData.website}
                onChange={handleChange}
                autoComplete="off"
                tabIndex="-1"
              />
            </div>

            <FormGrid>
              <FieldGroup>
                <Label htmlFor="name">Name</Label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder="Your name"
                />
              </FieldGroup>

              <FieldGroup>
                <Label htmlFor="email">Email</Label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder="you@example.com"
                />
              </FieldGroup>
            </FormGrid>

            <FieldGroup>
              <Label htmlFor="message">Message</Label>
              <TextArea
                id="message"
                name="message"
                rows="6"
                value={formData.message}
                onChange={handleChange}
                required
                disabled={loading}
                placeholder="Write your message here..."
              />
            </FieldGroup>

            <SubmitButton type="submit" disabled={loading}>
              {loading && (
                <LoadingIcon>
                  <FaHourglassHalf size={18} />
                </LoadingIcon>
              )}
              {loading ? 'Sending...' : 'Submit'}
            </SubmitButton>
          </Form>

          <AltContact>
            Alternatively, you can reach me directly via{' '}
            <a href="mailto:kyriakos.papadopoulos@acg.edu?subject=Contact%20Inquiry&body=Hello%20Kyriakos,%0D%0A%0D%0AI%20would%20like%20to%20inquire%20about...%0D%0A%0D%0AThank%20you,%0D%0A[Your%20Name]">
              email
            </a>.
          </AltContact>
        </>
      )}
    </ContactContainer>
  );
};

export default Contact;